import { ELightPath } from "../Model/CaptureProfile";
import { Collection } from "../Model/Collection";
import {
  FilterCubesCalibration,
  ImagerCalibration,
  ObjectiveZoomCalibration,
  PixelSizeCalibration,
  PolarizerOffsetsCalibration,
} from "../Model/ImagerCalibration";
import {
  ImagerCapability,
  EColorFormat,
  ObjectiveZoom,
} from "../Model/ImagerCapabilities";
import {
  ImagerState,
  EImagingModuleStatus,
  ESuiteMode,
  SuiteState,
  PsrState,
  EPsrState,
} from "../Model/ModuleState";
import { Module } from "../Model/Module";
import { DisplayValue } from "../components/PlateTypeEditor/DefaultImagingSettingViews/DefaultImagingSettingComboBox";
import LightPathUtil from "./LightPathUtil";
import { FlatFieldCalibration } from "../Model/ImagerCalibration";
import { EImagingMode } from "../Model/EImagingMode";
import { PolarizerCalibration } from "../components/Settings/Imager/CameraCalibration/PolarizeOffsets";

export interface OpticMotorCalibration {
  objectiveZoomCalibration: ObjectiveZoomCalibration | null;
  filterCubesCalibration: FilterCubesCalibration | null;
}

class ImagerUtil {
  static isPolarizerCalibrationNotZero(updatedPolarizerCalibration: PolarizerCalibration): boolean {
    return (
      (updatedPolarizerCalibration.off.x !== 0 ||
        updatedPolarizerCalibration.off.y !== 0 ||
        updatedPolarizerCalibration.off.z !== 0) &&
      (updatedPolarizerCalibration.on.x !== 0 ||
        updatedPolarizerCalibration.on.y !== 0 ||
        updatedPolarizerCalibration.on.z !== 0)
    );
  }
  static toPolarizerOffsetCalibration(polarizerCalibrationData: PolarizerCalibration) {
    return {
      offset: {
        x: polarizerCalibrationData.on.x - polarizerCalibrationData.off.x,
        y: polarizerCalibrationData.on.y - polarizerCalibrationData.off.y,
        z: polarizerCalibrationData.on.z - polarizerCalibrationData.off.z,
      },
    };
  }

  static getIsPlateOnImager(imagerState: ImagerState) {
    return (
      imagerState.state === EImagingModuleStatus.Imageable ||
      imagerState.state === EImagingModuleStatus.Imaging ||
      imagerState.state === EImagingModuleStatus.Busy ||
      imagerState.state == EImagingModuleStatus.Cancelling
    );
  }

  static getIsOpticControlDisabled(
    imagerState: ImagerState,
    suiteState: SuiteState
  ) {
    return (
      (imagerState.state !== EImagingModuleStatus.Imageable &&
        imagerState.state !== EImagingModuleStatus.Idle) ||
      suiteState.suiteMode !== ESuiteMode.Normal ||
      suiteState.imagingMode === EImagingMode.Automatic
    );
  }

  static getIsAdditionalOpticControlDisabled(
    imagerState: ImagerState,
    suiteState: SuiteState
  ) {
    return (
      imagerState.state !== EImagingModuleStatus.Imageable ||
      suiteState.suiteMode !== ESuiteMode.Normal ||
      suiteState.imagingMode === EImagingMode.Automatic
    );
  }

  static getIsWellControlDisabled(
    imagerState: ImagerState,
    suiteState: SuiteState
  ) {
    return (
      imagerState.state !== EImagingModuleStatus.Imageable ||
      suiteState.suiteMode !== ESuiteMode.Normal ||
      suiteState.imagingMode === EImagingMode.Automatic
    );
  }

  static getObjectiveZoomDisplayValues(objectiveZoom: ObjectiveZoom) {
    return objectiveZoom.objectives.map((objective) => {
      return {
        value: objective.value.toFixed(1),
        display: objective.name.toString(),
      };
    });
  }

  static getImagerOptions(imagerModules: Module[]): DisplayValue[] {
    return imagerModules.map((module) => {
      return {
        value: module.identifier,
        display: module.name,
      };
    });
  }

  static getLightpathOptions(
    imagerCapabilities: ImagerCapability
  ): DisplayValue[] {
    return imagerCapabilities.capabilities.map((capability) => {
      return {
        value: capability.lightpath.toString(),
        display: LightPathUtil.toName(capability.lightpath),
      };
    });
  }

  static getLightPathNumberOptions(imagerCapabilities: ImagerCapability) {
    return imagerCapabilities.capabilities.map((capability) => {
      return {
        value: capability.lightpath,
        display: LightPathUtil.toName(capability.lightpath),
      };
    });
  }

  static getFlatFieldCalibration(
    imagerCalibration: ImagerCalibration | null | undefined,
    lightPath: ELightPath
  ): FlatFieldCalibration | null {
    let flatFieldCalibration: FlatFieldCalibration | undefined = undefined;

    if (imagerCalibration === null || imagerCalibration === undefined) {
      return null;
    }

    switch (lightPath) {
      case ELightPath.Fluorescence: {
        flatFieldCalibration = imagerCalibration.fluorescence?.flatField;
        break;
      }
      case ELightPath.Ultraviolet: {
        flatFieldCalibration = imagerCalibration.ultraviolet?.flatField;
        break;
      }
      case ELightPath.Visible: {
        flatFieldCalibration = imagerCalibration.visible?.flatField;
        break;
      }
      default: {
        throw new Error(`LightPath ${ELightPath[lightPath]} is not supported`);
      }
    }

    if (flatFieldCalibration == undefined) {
      // throw new Error("Flat field calibration is null");
      return null;
    }

    return flatFieldCalibration;
  }

  static getPixelSizeCalibration(
    imagerCalibration: ImagerCalibration | undefined,
    lightPath: ELightPath
  ): PixelSizeCalibration | undefined {
    let pixelSizeCalibration: PixelSizeCalibration | undefined = undefined;
    if (imagerCalibration === undefined) {
      return pixelSizeCalibration;
    }
    switch (lightPath) {
      case ELightPath.Fluorescence: {
        pixelSizeCalibration = imagerCalibration.fluorescence?.pixelSize;
        break;
      }
      case ELightPath.Ultraviolet: {
        pixelSizeCalibration = imagerCalibration.ultraviolet?.pixelSize;
        break;
      }
      case ELightPath.Visible: {
        pixelSizeCalibration = imagerCalibration.visible?.pixelSize;
        break;
      }
      default: {
        throw new Error(`LightPath ${ELightPath[lightPath]} is not supported`);
      }
    }

    // if (pixelSizeCalibration == null) {
    //   throw new Error("Pixel size calibration is null");
    // }

    return pixelSizeCalibration;
  }

  static getObjectiveValue(
    imagerCapabilities: ImagerCapability,
    imagerState: ImagerState,
    lightPath: ELightPath
  ) {
    let objectiveZoom: number = -1;
    let objectiveIndex: number = -1;
    switch (lightPath) {
      case ELightPath.Fluorescence: {
        objectiveZoom =
          imagerState.captureProfile.fluorescence?.objectiveZoom ?? -1;
        break;
      }
      case ELightPath.Ultraviolet: {
        objectiveZoom =
          imagerState.captureProfile.ultraviolet?.objectiveZoom ?? -1;
        break;
      }
      case ELightPath.Visible: {
        objectiveZoom = imagerState.captureProfile.visible?.objectiveZoom ?? -1;
        break;
      }
      default: {
        throw new Error(`LightPath ${ELightPath[lightPath]} is not supported`);
      }
    }

    objectiveIndex =
      imagerCapabilities.capabilities
        .find((capabilities) => capabilities.lightpath === lightPath)
        ?.optic?.objectiveZoom?.objectives.findIndex(
          (objective) => objective.value === objectiveZoom
        ) ?? -1;

    return {
      objectiveZoom: objectiveZoom,
      objectiveIndex: objectiveIndex,
    };
  }

  static getFilterCubeValue(
    imagerCapabilities: ImagerCapability,
    imagerState: ImagerState,
    lightPath: ELightPath
  ) {
    let filterCubeId: number = -1;
    let filterCubeIndex: number = -1;
    switch (lightPath) {
      case ELightPath.Fluorescence: {
        filterCubeId =
          imagerState.captureProfile.fluorescence?.filterCubeId ?? -1;
        break;
      }
      case ELightPath.Ultraviolet: {
        filterCubeId = -1;
        break;
      }
      case ELightPath.Visible: {
        filterCubeId = -1;
        break;
      }
      default: {
        throw new Error(`LightPath ${ELightPath[lightPath]} is not supported`);
      }
    }

    filterCubeIndex =
      imagerCapabilities.capabilities
        .find((capabilities) => capabilities.lightpath === lightPath)
        ?.optic?.filterCubes?.filterCubes.findIndex(
          (filterCube) => filterCube.id === filterCubeId
        ) ?? -1;

    return {
      filterCubeId: filterCubeId,
      filterCubeIndex: filterCubeIndex,
    };
  }

  static getObjectiveIndex(
    imagerCapabilities: ImagerCapability,
    objectiveValue: number,
    lightPath: ELightPath
  ): number {
    return (
      imagerCapabilities.capabilities
        .find((capabilities) => capabilities.lightpath === lightPath)
        ?.optic?.objectiveZoom?.objectives.findIndex(
          (objective) => objective.value === objectiveValue
        ) ?? -1
    );
  }

  static getFilterCubeIndex(
    imagerCapabilities: ImagerCapability,
    filterCubeId: number,
    lightPath: ELightPath
  ): number {
    return (
      imagerCapabilities.capabilities
        .find((capabilities) => capabilities.lightpath === lightPath)
        ?.optic?.filterCubes?.filterCubes.findIndex(
          (filterCube) => filterCube.id === filterCubeId
        ) ?? -1
    );
  }

  static getOpticMotorCalibration(
    imagerCalibration: ImagerCalibration | null | undefined,
    lightPath: ELightPath
  ): OpticMotorCalibration {
    let objectiveZoomCalibration: ObjectiveZoomCalibration | null = null;
    let filterCubesCalibration: FilterCubesCalibration | null = null;

    if (imagerCalibration === null || imagerCalibration === undefined) {
      return {
        objectiveZoomCalibration: null,
        filterCubesCalibration: null,
      };
    }

    switch (lightPath) {
      case ELightPath.Fluorescence: {
        filterCubesCalibration =
          imagerCalibration.fluorescence?.filterCubes ?? null;
        objectiveZoomCalibration =
          imagerCalibration.fluorescence?.objectiveZoom ?? null;
        break;
      }
      case ELightPath.Ultraviolet: {
        objectiveZoomCalibration =
          imagerCalibration.ultraviolet?.objectiveZoom ?? null;
        break;
      }
      case ELightPath.Visible: {
        objectiveZoomCalibration =
          imagerCalibration.visible?.objectiveZoom ?? null;
        break;
      }
      default: {
        throw new Error(`LightPath ${ELightPath[lightPath]} is not supported`);
      }
    }

    return {
      objectiveZoomCalibration: objectiveZoomCalibration,
      filterCubesCalibration: filterCubesCalibration,
    };
  }

  static getObjectiveZoomCalibration(
    imagerCalibration: ImagerCalibration | null | undefined,
    lightPath: ELightPath
  ): ObjectiveZoomCalibration | null {
    let objectiveZoom: ObjectiveZoomCalibration | null = null;

    if (imagerCalibration === null || imagerCalibration === undefined) {
      return objectiveZoom;
    }

    switch (lightPath) {
      case ELightPath.Fluorescence: {
        objectiveZoom = imagerCalibration.fluorescence?.objectiveZoom ?? null;
        break;
      }
      case ELightPath.Ultraviolet: {
        objectiveZoom = imagerCalibration.ultraviolet?.objectiveZoom ?? null;
        break;
      }
      case ELightPath.Visible: {
        objectiveZoom = imagerCalibration.visible?.objectiveZoom ?? null;
        break;
      }
      default: {
        throw new Error(`LightPath ${ELightPath[lightPath]} is not supported`);
      }
    }

    // if (objectiveZoom == null) {
    //   throw new Error("Objective zoom is null");
    // }

    return objectiveZoom;
  }

  static getPolarizerOffsets(
    imagerCalibration: ImagerCalibration | null | undefined,
    lightPath: ELightPath
  ): PolarizerOffsetsCalibration | null {
    let polarizerOffsets: PolarizerOffsetsCalibration | null = null;

    if (imagerCalibration === null || imagerCalibration === undefined) {
      return polarizerOffsets;
    }

    switch (lightPath) {
      case ELightPath.Visible: {
        polarizerOffsets = imagerCalibration.visible?.polarizer ?? null;
        break;
      }
      default: {
        throw new Error(`LightPath ${ELightPath[lightPath]} is not supported`);
      }
    }

    // if (objectiveZoom == null) {
    //   throw new Error("Objective zoom is null");
    // }

    return polarizerOffsets;
  }

  static getImagerIdentifiersWithObjective(
    imagerCapabilitiesCollection: Collection<ImagerCapability>
  ): string[] {
    return imagerCapabilitiesCollection.allIds.filter((imagerIdentifier) => {
      const imagerCapability =
        imagerCapabilitiesCollection.byId[imagerIdentifier];
      const lightPathWithObjective =
        imagerCapability.lightpathCollection.allIds.filter(
          (lightPath) =>
            imagerCapability.lightpathCollection.byId[lightPath].optic
              .objectiveZoom != null
        );
      return lightPathWithObjective.length > 0;
    });
  }

  static getTotalLightpathCount(
    imagerCapabilitiesCollection: Collection<ImagerCapability>
  ): number {
    let count = 0;
    imagerCapabilitiesCollection.allIds.forEach((identifier) => {
      count +=
        imagerCapabilitiesCollection.byId[identifier].lightpathCollection.allIds
          .length;
    });
    return count;
  }

  static getImagerIdentifiersWithFlatFieldCorrection(
    imagerCapabilitiesCollection: Collection<ImagerCapability>
  ): string[] {
    return imagerCapabilitiesCollection.allIds.filter((imagerIdentifier) => {
      const imagerCapability =
        imagerCapabilitiesCollection.byId[imagerIdentifier];
      const lightPathWithFlatField =
        ImagerUtil.getLightPathWithFlatField(imagerCapability);
      return lightPathWithFlatField !== ELightPath.Disabled;
    });
  }

  static getImagerIdentifiersWithFilterCubes(
    imagerCapabilitiesCollection: Collection<ImagerCapability>
  ): string[] {
    return imagerCapabilitiesCollection.allIds.filter((imagerIdentifier) => {
      const imagerCapability =
        imagerCapabilitiesCollection.byId[imagerIdentifier];
      const lightPathWithFilterCube =
        imagerCapability.lightpathCollection.allIds.filter(
          (lightPath) =>
            imagerCapability.lightpathCollection.byId[lightPath].optic
              .filterCubes != null
        );
      return lightPathWithFilterCube.length > 0;
    });
  }

  static getImagerIdentifiersWithOpticMotor(
    imagerCapabilitiesCollection: Collection<ImagerCapability>
  ): string[] {
    return imagerCapabilitiesCollection.allIds.filter((imagerIdentifier) => {
      const imagerCapability =
        imagerCapabilitiesCollection.byId[imagerIdentifier];
      const lightPathWithFilterCube =
        imagerCapability.lightpathCollection.allIds.filter(
          (lightPath) =>
            imagerCapability.lightpathCollection.byId[lightPath].optic
              .filterCubes != null
        );
      const lightPathWithObjective =
        imagerCapability.lightpathCollection.allIds.filter(
          (lightPath) =>
            imagerCapability.lightpathCollection.byId[lightPath].optic
              .objectiveZoom != null
        );
      return (
        lightPathWithFilterCube.length > 0 || lightPathWithObjective.length > 0
      );
    });
  }

  static getLightPathWithFlatField(
    imagerCapability: ImagerCapability
  ): ELightPath {
    let result: ELightPath = ELightPath.Disabled;
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].camera
          .colorFormat === EColorFormat.Grayscale
      ) {
        result = lightPath;
        break;
      }
    }
    return result;
  }

  static getActiveLightPathSupportFlatField(
    imagerCapability: ImagerCapability,
    activeLightPath: ELightPath
  ): boolean {
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].camera
          .colorFormat === EColorFormat.Grayscale &&
        activeLightPath === lightPath
      ) {
        return true;
      }
    }
    return false;
  }

  static getMultipleLightPathWithFlatField(
    imagerCapability: ImagerCapability
  ): ELightPath[] {
    let result: ELightPath[] = [];
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].camera
          .colorFormat === EColorFormat.Grayscale
      ) {
        result.push(lightPath);
      }
    }
    return result;
  }

  static getActiveLightPathSupportObjectiveZoom(
    imagerCapability: ImagerCapability,
    activeLightPath: ELightPath
  ): boolean {
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .objectiveZoom != null &&
        activeLightPath === lightPath
      ) {
        return true;
      }
    }

    return false;
  }

  static getMultipleLightPathWithObjective(
    imagerCapability: ImagerCapability
  ): ELightPath[] {
    let result: ELightPath[] = [];
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .objectiveZoom != null
      ) {
        result.push(lightPath);
        break;
      }
    }

    return result;
  }

  static getLightPathWithObjective(
    imagerCapability: ImagerCapability
  ): ELightPath {
    let result = ELightPath.Disabled;
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .objectiveZoom != null
      ) {
        result = lightPath;
        break;
      }
    }

    return result;
  }

  static getLightPathWithFilterCube(
    imagerCapability: ImagerCapability
  ): ELightPath {
    let result = ELightPath.Disabled;
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .filterCubes != null
      ) {
        result = lightPath;
        break;
      }
    }

    if (result === ELightPath.Disabled) {
      throw new Error(
        `Imager ${imagerCapability.identifier} doesn't has a filter cubes`
      );
    }

    return result;
  }

  static getActiveLightPathHasOpticMotor(
    imagerCapability: ImagerCapability,
    activeLightPath: ELightPath
  ) {
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .objectiveZoom != null &&
        activeLightPath === lightPath
      ) {
        return true;
      }
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .filterCubes != null &&
        activeLightPath === lightPath
      ) {
        return true;
      }
    }

    return false;
  }

  static getLightPathWithOpticMotor(
    imagerCapability: ImagerCapability
  ): ELightPath {
    let result = ELightPath.Disabled;
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .objectiveZoom != null
      ) {
        result = lightPath;
        break;
      }
      if (
        imagerCapability.lightpathCollection.byId[lightPath].optic
          .filterCubes != null
      ) {
        result = lightPath;
        break;
      }
    }

    return result;
  }

  static getLightPathWithWhiteBalance(
    imagerCapability: ImagerCapability
  ): ELightPath {
    let result = ELightPath.Disabled;
    for (const lightPath of imagerCapability.lightpathCollection.allIds) {
      if (
        imagerCapability.lightpathCollection.byId[lightPath].camera
          .colorFormat === EColorFormat.Bayer ||
        imagerCapability.lightpathCollection.byId[lightPath].camera
          .colorFormat === EColorFormat.Color
      ) {
        result = lightPath;
        break;
      }
    }

    return result;
  }
}

export default ImagerUtil;
